import type { DrawMatch, MatchProgression } from '@/draw/types';
import type { Ladder } from './ladderModel';
import type { DrawGroup } from './drawSelection';

export interface MatchDestinations {
  winner?: string;
  loser?: string;
}

/**
 * What the public draw needs to trace paths: where each match sends its winner and loser, which
 * stage group (chip) shows each match, and which groups each player appears in.
 */
export interface DrawPathIndex {
  destinations: Map<string, MatchDestinations>;
  groupOfMatch: Map<string, string>;
  groupsOfPlayer: Map<string, Set<string>>;
}

function ladderMatches(ladder: Ladder): DrawMatch[] {
  return ladder.rounds.flatMap((r) => [...r.matches.map((lm) => lm.match), ...r.feeders.flatMap(ladderMatches)]);
}

export function buildDrawPathIndex(progressions: MatchProgression[] | undefined, groups: DrawGroup[]): DrawPathIndex {
  const destinations = new Map<string, MatchDestinations>();
  for (const p of progressions ?? []) {
    const d = destinations.get(p.fromMatchId) ?? {};
    d[p.outcome] = p.toMatchId;
    destinations.set(p.fromMatchId, d);
  }

  const groupOfMatch = new Map<string, string>();
  const groupsOfPlayer = new Map<string, Set<string>>();
  for (const group of groups) {
    for (const section of group.sections) {
      const matches =
        section.kind === 'pool' ? section.stage.rounds.flatMap((r) => r.matches) : ladderMatches(section.ladder);
      for (const match of matches) {
        groupOfMatch.set(match.matchId, group.key);
        // A bye has no loser, so it leads nowhere on the losing side (its loser link is hidden too).
        if (match.isBye || match.player1?.isBye || match.player2?.isBye) delete destinations.get(match.matchId)?.loser;
        for (const player of [match.player1, match.player2]) {
          if (!player || player.isBye) continue;
          const set = groupsOfPlayer.get(player.id) ?? new Set<string>();
          set.add(group.key);
          groupsOfPlayer.set(player.id, set);
        }
      }
    }
  }
  return { destinations, groupOfMatch, groupsOfPlayer };
}

/** Groups other than the one on screen that the hovered team or match leads to (their chips glow). */
export function hintedGroups(
  index: DrawPathIndex,
  current: string | null,
  hover: { team: string | null; match: string | null },
): Set<string> {
  const keys = new Set<string>();
  if (hover.team) for (const key of index.groupsOfPlayer.get(hover.team) ?? []) keys.add(key);
  if (hover.match) {
    const d = index.destinations.get(hover.match);
    for (const target of [d?.winner, d?.loser]) {
      const key = target ? index.groupOfMatch.get(target) : undefined;
      if (key) keys.add(key);
    }
  }
  if (current) keys.delete(current);
  return keys;
}
